import CryptoJS from 'crypto-js';

const COGNITO_KEY_PREFIX = 'CognitoIdentityServiceProvider';
const ENCRYPTION_KEY_STORAGE_KEY = '__amw_cognito_enc_key';

class EncryptedCognitoStorage {
  constructor() {
    // Tokens live in sessionStorage so they are scoped to a single tab and are
    // discarded when it closes. The key is persisted next to them so a reload
    // within the same tab can still decrypt.
    this.storage = sessionStorage;
    this.encryptionKey = this.resolveKey();
    this.purgeLegacyLocalStorage();
  }

  /**
   * Get the encryption key for this browsing session, creating one if needed
   */
  resolveKey() {
    try {
      const existing = this.storage.getItem(ENCRYPTION_KEY_STORAGE_KEY);
      if (existing) {
        return existing;
      }
      const key = this.generateKey();
      this.storage.setItem(ENCRYPTION_KEY_STORAGE_KEY, key);
      return key;
    } catch (error) {
      // sessionStorage unavailable - fall back to a key valid for this page only
      return this.generateKey();
    }
  }

  /**
   * Generate a cryptographically random 256-bit key
   */
  generateKey() {
    if (window.crypto && window.crypto.getRandomValues) {
      const bytes = new Uint8Array(32);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    }
    return CryptoJS.lib.WordArray.random(32).toString();
  }

  /**
   * Remove Cognito data written to localStorage by earlier versions
   */
  purgeLegacyLocalStorage() {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(COGNITO_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('legacy purge error:', error);
    }
  }

  /**
   * Encrypt and store item
   */
  setItem(key, value) {
    try {
      if (this.shouldEncrypt(key)) {
        // Encrypt Cognito data (tokens, userData, LastAuthUser)
        const encrypted = CryptoJS.AES.encrypt(value, this.encryptionKey).toString();
        this.storage.setItem(key, encrypted);
      } else {
        // Store non-sensitive data as-is (like clockDrift)
        this.storage.setItem(key, value);
      }
      return value;
    } catch (error) {
      console.error('setItem error:', error);
      throw error;
    }
  }

  /**
   * Retrieve and decrypt item
   */
  getItem(key) {
    try {
      const value = this.storage.getItem(key);
      if (!value) return null;

      if (this.shouldEncrypt(key)) {
        // Decrypt Cognito data
        try {
          const bytes = CryptoJS.AES.decrypt(value, this.encryptionKey);
          const decrypted = bytes.toString(CryptoJS.enc.Utf8);

          if (!decrypted) {
            // Unreadable ciphertext - drop it so Amplify treats the user as
            // signed out rather than consuming raw ciphertext.
            this.storage.removeItem(key);
            return null;
          }
          return decrypted;
        } catch (decryptError) {
          this.storage.removeItem(key);
          return null;
        }
      }

      // Return non-sensitive data as-is (like clockDrift)
      return value;
    } catch (error) {
      return null;
    }
  }

  /**
   * Remove item
   */
  removeItem(key) {
    try {
      this.storage.removeItem(key);
    } catch (error) {
        console.error('error:', error);
    }
  }

  /**
   * Clear all storage
   */
  clear() {
    try {
      // Clear all Cognito keys
      const keysToRemove = [];
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i);
        if (key && key.startsWith(COGNITO_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => this.storage.removeItem(key));
      this.storage.removeItem(ENCRYPTION_KEY_STORAGE_KEY);
      this.encryptionKey = this.resolveKey();
    } catch (error) {
      console.error('clear error:', error);
    }
  }

  /**
   * Determine if key should be encrypted
   */
  shouldEncrypt(key) {
    // Encrypt ALL Cognito-related data (tokens, LastAuthUser, userData, etc.)
    // Only skip clockDrift as it's not sensitive (just a timestamp offset)
    return key.includes(COGNITO_KEY_PREFIX) && 
           !key.includes('clockDrift');
  }

  /**
   * Mask key for logging (security)
   */
  maskKey(key) {
    if (key.length > 50) {
      return key.substring(0, 40) + '...' + key.substring(key.length - 15);
    }
    return key;
  }

  /**
   * Debug: Check what's encrypted
   */
  getEncryptedKeys() {
    const encrypted = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key && this.shouldEncrypt(key)) {
        encrypted.push(key);
      }
    }
    return encrypted;
  }
}


// Export singleton instance
export default new EncryptedCognitoStorage();